// StudioQueue.jsx — /preservation
// Staff view for the preservation pipeline. Shows all orders with
// isPreservation=true that have entered the preservation flow (state: "done"),
// grouped by preservationStatus. Also shows shopOrders with isCommission
// for backward compat with orders created before the unified queue.

import { useEffect, useState } from "react";
import { collection, onSnapshot, query, where, doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db, auth } from "../firebase";
import { STAFF_THEME, FONT } from "../theme/staff";
import { trashOrder, trashShopOrder } from "../utils/trash";

const BRAND = STAFF_THEME;

const PRESERVATION_STATES = [
  { id: "pending",         label: "Pending",          emoji: "📋", description: "Awaiting reference photo or queued for drawing" },
  { id: "drawing",         label: "Drawing",          emoji: "✏️",  description: "In progress — artist is working" },
  { id: "drying",          label: "Drying",           emoji: "☀️",  description: "Pancake completed, drying before sealing" },
  { id: "sealing_coat_1",  label: "Sealing (Coat 1)", emoji: "🖌",  description: "First coat of mod podge applied" },
  { id: "sealing_coat_2",  label: "Sealing (Coat 2)", emoji: "🖌",  description: "Second coat applied" },
  { id: "flattening",      label: "Flattening",       emoji: "📐", description: "Under weight to flatten" },
  { id: "framing",         label: "Framing",          emoji: "🖼",  description: "Frame selection and preparation" },
  { id: "mounting",        label: "Mounting",         emoji: "🔧", description: "Mounting pancake in frame" },
  { id: "packaging",       label: "Packaging",        emoji: "📦", description: "Ready to pack and ship" },
  { id: "shipped",         label: "Shipped",          emoji: "✈️",  description: "On its way to the customer" },
];

function getStateIndex(status) {
  return PRESERVATION_STATES.findIndex(s => s.id === status);
}

function getNextState(status) {
  const idx = getStateIndex(status);
  return idx >= 0 && idx < PRESERVATION_STATES.length - 1
    ? PRESERVATION_STATES[idx + 1].id
    : null;
}

export default function StudioQueue({ navigate }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("active"); // "active" | "shipped" | "all"

  useEffect(() => {
    // Query orders collection for preservation items
    const q = query(
      collection(db, "orders"),
      where("isPreservation", "==", true)
    );
    const unsub1 = onSnapshot(q, (snap) => {
      const fromOrders = snap.docs
        .map(d => ({ id: d.id, ...d.data(), _collection: "orders" }))
        .filter(o => !o.trashedAt);
      mergeResults(fromOrders, []);
    });
    // Also query shopOrders for backward compat (commissions created before unified queue)
    const q2 = query(
      collection(db, "shopOrders"),
      where("isCommission", "==", true)
    );
    let shopResults = [];
    const unsub2 = onSnapshot(q2, (snap) => {
      shopResults = snap.docs
        .map(d => ({ id: d.id, ...d.data(), _collection: "shopOrders" }))
        // Exclude shopOrders that have a linked orders doc (avoid duplicates)
        .filter(o => !o.orderId)
        .filter(o => !o.trashedAt);
      mergeResults(null, shopResults);
    });

    let orderResults = [];
    function mergeResults(newOrders, newShop) {
      if (newOrders) orderResults = newOrders;
      if (newShop) shopResults = newShop;
      const merged = [...orderResults, ...shopResults]
        .sort((a, b) => (b.paidAt?.toMillis?.() ?? b.createdAt?.toMillis?.() ?? 0) - (a.paidAt?.toMillis?.() ?? a.createdAt?.toMillis?.() ?? 0));
      setOrders(merged);
      setLoading(false);
      if (selected) {
        const updated = merged.find(o => o.id === selected.id);
        if (updated) setSelected(updated);
      }
    }

    return () => { unsub1(); unsub2(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const displayed = orders.filter(o => {
    if (filter === "active") return o.preservationStatus !== "shipped";
    if (filter === "shipped") return o.preservationStatus === "shipped";
    return true;
  });

  async function advanceStatus(order) {
    const next = getNextState(order.preservationStatus || "pending");
    if (!next) return;
    const coll = order._collection || "orders";
    await updateDoc(doc(db, coll, order.id), {
      preservationStatus: next,
      [`preservationTimestamps.${next}`]: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }

  async function setStatus(order, status) {
    const coll = order._collection || "orders";
    await updateDoc(doc(db, coll, order.id), {
      preservationStatus: status,
      [`preservationTimestamps.${status}`]: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }

  async function trashCommission(order) {
    const name = order.commissionGuestName || order.customerName || "this commission";
    if (!window.confirm(`Move ${name} to trash? Recoverable for 30 days.`)) return;
    const email = auth.currentUser?.email || null;
    if (order._collection === "shopOrders") {
      await trashShopOrder(order.id, email);
    } else {
      await trashOrder(order.id, email);
    }
    setSelected(null);
  }

  if (selected) {
    return <CommissionDetail order={selected} onBack={() => setSelected(null)} onAdvance={advanceStatus} onSetStatus={setStatus} onTrash={trashCommission} />;
  }

  return (
    <div style={{ minHeight: "100vh", background: BRAND.dark, fontFamily: FONT.body, display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div style={{ padding: "0.75rem 1rem", background: BRAND.panel, borderBottom: `1px solid ${BRAND.border}`, display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
        <div style={{ color: BRAND.gold, fontWeight: "bold", fontSize: "1.1rem" }}>🖌 Preservation</div>
        <div style={{ color: BRAND.muted, fontSize: "0.78rem", flex: 1 }}>
          {loading ? "" : `${displayed.length} commission${displayed.length !== 1 ? "s" : ""}`}
        </div>
        <button onClick={() => navigate("/menu")} style={hudBtn}>← Menu</button>
      </div>

      {/* Filter tabs */}
      <div style={{ display: "flex", gap: "0.4rem", padding: "0.5rem 1rem", background: BRAND.panel, borderBottom: `1px solid ${BRAND.divider}` }}>
        {[
          { id: "active", label: "Active" },
          { id: "shipped", label: "Shipped" },
          { id: "all", label: "All" },
        ].map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            style={{
              background: filter === f.id ? BRAND.gold : "transparent",
              color: filter === f.id ? BRAND.dark : BRAND.muted,
              border: filter === f.id ? `1px solid ${BRAND.gold}` : `1px solid ${BRAND.border}`,
              borderRadius: "8px", padding: "0.35rem 0.75rem",
              fontSize: "0.82rem", fontWeight: filter === f.id ? 800 : 600,
              cursor: "pointer",
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Order list */}
      <div style={{ flex: 1, overflowY: "auto", padding: "0.75rem" }}>
        {loading ? (
          <div style={{ color: BRAND.dim, textAlign: "center", padding: "3rem" }}>Loading...</div>
        ) : displayed.length === 0 ? (
          <div style={{ color: BRAND.dim, textAlign: "center", padding: "3rem", fontSize: "0.95rem" }}>
            {filter === "active" ? "No active commissions" : filter === "shipped" ? "No shipped commissions yet" : "No commissions yet"}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            {displayed.map(order => (
              <CommissionCard key={order.id} order={order} onTap={() => setSelected(order)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── COMMISSION CARD ─────────────────────────────────────────────────────────
function CommissionCard({ order, onTap }) {
  const status = PRESERVATION_STATES.find(s => s.id === (order.preservationStatus || "pending"));
  const hasRef = !!order.commissionImageUrl;
  const name = order.commissionGuestName || order.customerName || "Guest";
  const date = order.paidAt?.toDate ? order.paidAt.toDate().toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";

  return (
    <button onClick={onTap} style={{
      background: BRAND.card, border: `1px solid ${BRAND.border}`, borderRadius: "12px",
      padding: "0.75rem", display: "flex", gap: "0.75rem", alignItems: "center",
      cursor: "pointer", textAlign: "left", width: "100%",
    }}>
      {/* Reference thumbnail */}
      <div style={{
        width: "60px", height: "60px", borderRadius: "10px", flexShrink: 0,
        background: hasRef ? "#000" : "rgba(255,255,255,0.05)",
        display: "flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden",
      }}>
        {hasRef ? (
          <img src={order.commissionImageUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <span style={{ fontSize: "1.5rem", opacity: 0.3 }}>📸</span>
        )}
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}>
          <span style={{ color: "white", fontWeight: 700, fontSize: "0.95rem" }}>{name}</span>
          {date && <span style={{ color: BRAND.dim, fontSize: "0.72rem" }}>{date}</span>}
        </div>
        {order.commissionNotes && (
          <div style={{ color: BRAND.muted, fontSize: "0.78rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: "0.15rem" }}>
            {order.commissionNotes}
          </div>
        )}
        {!hasRef && (
          <div style={{ color: BRAND.orange, fontSize: "0.75rem", fontWeight: 700, marginTop: "0.15rem" }}>
            Awaiting reference photo
          </div>
        )}
      </div>

      {/* Status badge */}
      <div style={{
        background: "rgba(252,179,31,0.12)", border: `1px solid rgba(252,179,31,0.3)`,
        borderRadius: "8px", padding: "0.25rem 0.5rem", flexShrink: 0,
        display: "flex", alignItems: "center", gap: "0.3rem",
      }}>
        <span style={{ fontSize: "0.85rem" }}>{status?.emoji}</span>
        <span style={{ color: BRAND.gold, fontSize: "0.7rem", fontWeight: 700 }}>{status?.label}</span>
      </div>
    </button>
  );
}

// ── COMMISSION DETAIL ───────────────────────────────────────────────────────
function CommissionDetail({ order, onBack, onAdvance, onSetStatus, onTrash }) {
  const status = PRESERVATION_STATES.find(s => s.id === (order.preservationStatus || "pending"));
  const statusIdx = getStateIndex(order.preservationStatus || "pending");
  const nextState = getNextState(order.preservationStatus || "pending");
  const nextLabel = nextState ? PRESERVATION_STATES.find(s => s.id === nextState)?.label : null;
  const name = order.commissionGuestName || order.customerName || "Guest";
  const hasRef = !!order.commissionImageUrl;

  return (
    <div style={{ minHeight: "100vh", background: BRAND.dark, fontFamily: FONT.body, display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div style={{ padding: "0.75rem 1rem", background: BRAND.panel, borderBottom: `1px solid ${BRAND.border}`, display: "flex", alignItems: "center", gap: "0.75rem" }}>
        <button onClick={onBack} style={hudBtn}>← Back</button>
        <div style={{ flex: 1, textAlign: "center" }}>
          <div style={{ color: BRAND.gold, fontWeight: "bold", fontSize: "1rem" }}>{name}</div>
          <div style={{ color: BRAND.dim, fontSize: "0.72rem" }}>{order.customerEmail}</div>
        </div>
        <div style={{ width: "60px" }} /> {/* spacer for centering */}
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "1rem" }}>
        {/* Reference image */}
        {hasRef && (
          <div style={{ marginBottom: "1rem", borderRadius: "14px", overflow: "hidden", border: `2px solid ${BRAND.border}` }}>
            <img src={order.commissionImageUrl} alt="Reference" style={{ width: "100%", display: "block" }} />
          </div>
        )}

        {!hasRef && (
          <div style={{
            background: "rgba(245,118,0,0.08)", border: `1px solid rgba(245,118,0,0.3)`,
            borderRadius: "12px", padding: "1.5rem", textAlign: "center", marginBottom: "1rem",
          }}>
            <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>📸</div>
            <div style={{ color: BRAND.orange, fontWeight: 700, fontSize: "0.95rem" }}>
              Reference photo not yet uploaded
            </div>
            <div style={{ color: BRAND.dim, fontSize: "0.82rem", marginTop: "0.3rem" }}>
              The guest received an email with the upload link
            </div>
          </div>
        )}

        {/* Guest notes */}
        {order.commissionNotes && (
          <div style={{ background: BRAND.card, border: `1px solid ${BRAND.border}`, borderRadius: "12px", padding: "0.85rem 1rem", marginBottom: "1rem" }}>
            <div style={{ color: BRAND.dim, fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "0.35rem", fontWeight: 700 }}>
              Guest notes
            </div>
            <div style={{ color: BRAND.cream, fontSize: "0.92rem", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
              {order.commissionNotes}
            </div>
          </div>
        )}

        {/* Order details */}
        <div style={{ background: BRAND.card, border: `1px solid ${BRAND.border}`, borderRadius: "12px", padding: "0.85rem 1rem", marginBottom: "1rem" }}>
          <div style={{ color: BRAND.dim, fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "0.5rem", fontWeight: 700 }}>
            Order details
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem", fontSize: "0.85rem" }}>
            <div>
              <div style={{ color: BRAND.dim, fontSize: "0.68rem" }}>Customer</div>
              <div style={{ color: "white" }}>{order.customerName || "—"}</div>
            </div>
            <div>
              <div style={{ color: BRAND.dim, fontSize: "0.68rem" }}>Email</div>
              <div style={{ color: "white", wordBreak: "break-all" }}>{order.customerEmail || "—"}</div>
            </div>
            <div>
              <div style={{ color: BRAND.dim, fontSize: "0.68rem" }}>Total</div>
              <div style={{ color: "white" }}>${((order.totalCents || 0) / 100).toFixed(2)}</div>
            </div>
            <div>
              <div style={{ color: BRAND.dim, fontSize: "0.68rem" }}>Ordered</div>
              <div style={{ color: "white" }}>{order.paidAt?.toDate ? order.paidAt.toDate().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}</div>
            </div>
          </div>
          {order.shippingAddress?.line1 && (
            <div style={{ marginTop: "0.6rem", borderTop: `1px solid ${BRAND.divider}`, paddingTop: "0.5rem" }}>
              <div style={{ color: BRAND.dim, fontSize: "0.68rem", marginBottom: "0.2rem" }}>Ship to</div>
              <div style={{ color: "white", fontSize: "0.85rem", lineHeight: 1.5 }}>
                {order.shippingAddress.line1}{order.shippingAddress.line2 ? `, ${order.shippingAddress.line2}` : ""}<br />
                {[order.shippingAddress.city, order.shippingAddress.state, order.shippingAddress.zip].filter(Boolean).join(", ")}
              </div>
            </div>
          )}
        </div>

        {/* Preservation pipeline */}
        <div style={{ background: BRAND.card, border: `1px solid ${BRAND.border}`, borderRadius: "12px", padding: "0.85rem 1rem", marginBottom: "1rem" }}>
          <div style={{ color: BRAND.dim, fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "0.6rem", fontWeight: 700 }}>
            Preservation progress
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
            {PRESERVATION_STATES.map((s, i) => {
              const isCurrent = s.id === (order.preservationStatus || "pending");
              const isDone = i < statusIdx;
              const ts = order.preservationTimestamps?.[s.id];
              return (
                <button
                  key={s.id}
                  onClick={() => onSetStatus(order, s.id)}
                  style={{
                    display: "flex", alignItems: "center", gap: "0.5rem",
                    padding: "0.4rem 0.6rem", borderRadius: "8px",
                    background: isCurrent ? "rgba(252,179,31,0.15)" : "transparent",
                    border: isCurrent ? `1px solid rgba(252,179,31,0.4)` : "1px solid transparent",
                    cursor: "pointer", width: "100%", textAlign: "left",
                  }}
                >
                  <span style={{ fontSize: "1rem", opacity: isDone || isCurrent ? 1 : 0.3 }}>{s.emoji}</span>
                  <span style={{
                    color: isCurrent ? BRAND.gold : isDone ? BRAND.green : BRAND.dim,
                    fontSize: "0.82rem", fontWeight: isCurrent ? 700 : 500,
                    flex: 1,
                  }}>
                    {s.label}
                    {isDone && " ✓"}
                  </span>
                  {ts?.toDate && (
                    <span style={{ color: BRAND.dim, fontSize: "0.65rem" }}>
                      {ts.toDate().toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Advance button */}
        {nextState && (
          <button
            onClick={() => onAdvance(order)}
            style={{
              width: "100%", background: BRAND.gold, color: BRAND.dark,
              border: "none", borderRadius: "12px", padding: "0.9rem",
              fontSize: "1rem", fontWeight: 800, cursor: "pointer",
              fontFamily: FONT.display,
            }}
          >
            {status?.emoji} Advance to: {nextLabel} →
          </button>
        )}

        {order.preservationStatus === "shipped" && (
          <div style={{
            textAlign: "center", padding: "1rem",
            color: BRAND.green, fontSize: "1rem", fontWeight: 700,
          }}>
            ✓ Commission complete and shipped
          </div>
        )}

        {/* Trash — soft delete, recoverable for 30 days */}
        <button
          onClick={() => onTrash(order)}
          style={{
            marginTop: "1.5rem", width: "100%",
            background: "transparent", color: BRAND.dim,
            border: `1px solid ${BRAND.border}`, borderRadius: "10px",
            padding: "0.65rem", fontSize: "0.82rem", fontWeight: 600,
            cursor: "pointer",
          }}
        >
          🗑 Move to trash
        </button>
      </div>
    </div>
  );
}

const hudBtn = {
  background: "transparent",
  border: `1px solid ${BRAND.border}`,
  color: BRAND.muted,
  borderRadius: "8px",
  padding: "0.4rem 0.7rem",
  fontSize: "0.78rem",
  fontWeight: 600,
  cursor: "pointer",
};
